/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getEOEventApplications } from "../../firebaseFunctions/getEOEventApplications/getEOEventApplications";
import { updateMPSApplication } from "../../firebaseFunctions/updateMPSApplication";
import { Grid3X3, Wand2, ChevronDown, ChevronRight, CheckCircle, Star } from "lucide-react";
import placeholderImage from "../../assets/images/eventImages/companyLogo.jpg";

const PLACEMENT_OPTIONS = [
  { value: "", label: "Standard" },
  { value: "premium", label: "⭐ Premium" },
  { value: "corner", label: "Corner" },
  { value: "near-stage", label: "Near Stage" },
  { value: "near-entrance", label: "Near Entrance" },
  { value: "near-food", label: "Near Food Court" },
];

const placementBadge = (val) => {
  if (!val) return null;
  const map = {
    premium: { label: "Premium", classes: "bg-amber-100 text-amber-700 border-amber-200" },
    corner: { label: "Corner", classes: "bg-blue-100 text-blue-700 border-blue-200" },
    "near-stage": { label: "Stage", classes: "bg-purple-100 text-purple-700 border-purple-200" },
    "near-entrance": { label: "Entrance", classes: "bg-green-100 text-green-700 border-green-200" },
    "near-food": { label: "Food Court", classes: "bg-orange-100 text-orange-700 border-orange-200" },
  };
  const m = map[val];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${m.classes}`}>
      {m.label}
    </span>
  );
};

const BoothsContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [boothEdits, setBoothEdits] = useState({});
  const [placementEdits, setPlacementEdits] = useState({});
  const [savingEvent, setSavingEvent] = useState(null);
  const [savedEvent, setSavedEvent] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return;
      setIsLoading(true);
      try {
        const res = await getEOEventApplications(user.uid);
        if (res.success && Array.isArray(res.data)) {
          // Only show approved/registered vendors in booth management
          const eventsWithApprovedVendors = res.data.map(e => ({
            ...e,
            applications: (e.applications || []).filter(app => {
              const s = app.applicationStatus?.toLowerCase();
              return s === "approved" || s === "registered";
            }),
          })).filter(e => e.applications.length > 0);
          setEvents(eventsWithApprovedVendors);

          // Pre-populate booth edits from existing data
          const edits = {};
          const placements = {};
          eventsWithApprovedVendors.forEach(event => {
            (event.applications || []).forEach(app => {
              edits[app.id] = app.boothNumber || "";
              placements[app.id] = app.boothPlacement || "";
            });
          });
          setBoothEdits(edits);
          setPlacementEdits(placements);

          // Auto-expand first event if there's only one
          if (eventsWithApprovedVendors.length === 1) {
            setExpandedEvents({ [eventsWithApprovedVendors[0].eventId]: true });
          }
        }
      } catch (err) {
        console.error("Error loading booth data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  // Fill in sequential numbers for unassigned vendors only
  const handleFillEmpty = (applications) => {
    const edits = { ...boothEdits };
    let next = 1;
    applications.forEach(app => {
      const n = parseInt(edits[app.id]);
      if (!isNaN(n) && n >= next) next = n + 1;
    });
    applications.forEach(app => {
      if (!edits[app.id]) {
        edits[app.id] = String(next++);
      }
    });
    setBoothEdits(edits);
  };

  // Overwrite all with fresh sequential numbers
  const handleReassignAll = (applications) => {
    const edits = { ...boothEdits };
    applications.forEach((app, i) => {
      edits[app.id] = String(i + 1);
    });
    setBoothEdits(edits);
  };

  const handleSaveEvent = async (eventId, applications) => {
    setSavingEvent(eventId);
    try {
      await Promise.all(
        applications.map(app =>
          updateMPSApplication({
            boothNumber: boothEdits[app.id] || "",
            boothPlacement: placementEdits[app.id] || "",
          }, app.id)
        )
      );
      // Update local state
      setEvents(prev =>
        prev.map(e =>
          e.eventId === eventId
            ? {
                ...e,
                applications: e.applications.map(a => ({
                  ...a,
                  boothNumber: boothEdits[a.id] || "",
                  boothPlacement: placementEdits[a.id] || "",
                })),
              }
            : e
        )
      );
      setSavedEvent(eventId);
      setTimeout(() => {
        setSavedEvent(null);
        navigate(`/eo/events/${eventId}`);
      }, 1500);
    } catch (err) {
      console.error("Error saving booth assignments:", err);
    } finally {
      setSavingEvent(null);
    }
  };

  const countAssigned = (applications) =>
    applications.filter(app => boothEdits[app.id] || app.boothNumber).length;

  const getStatusColor = (pct) => {
    if (pct === 100) return "bg-green-500";
    if (pct >= 50) return "bg-primary-500";
    return "bg-amber-400";
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Booth Management</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign and manage booth numbers for vendors across all your events
        </p>
      </div>

      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Grid3X3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-base font-medium text-gray-900 mb-1">No vendors to assign yet</h3>
          <p className="text-sm text-gray-500 max-w-sm">
            Once you approve vendor applications, you can assign them booth numbers here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => {
            const apps = event.applications || [];
            const assigned = countAssigned(apps);
            const pct = apps.length > 0 ? Math.round((assigned / apps.length) * 100) : 0;
            const isExpanded = !!expandedEvents[event.eventId];

            return (
              <div
                key={event.eventId}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white"
              >
                {/* Event header — click to expand/collapse */}
                <button
                  onClick={() => toggleExpand(event.eventId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`p-2 rounded-lg flex-shrink-0 ${
                        isExpanded ? "bg-primary-100" : "bg-gray-100"
                      }`}
                    >
                      <Grid3X3
                        className={`w-4 h-4 ${
                          isExpanded ? "text-primary-600" : "text-gray-500"
                        }`}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate text-sm">
                        {event.eventName || "Unnamed Event"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {assigned} of {apps.length}{" "}
                        {apps.length === 1 ? "vendor" : "vendors"} assigned
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {/* Progress bar */}
                    <div className="hidden sm:flex items-center gap-2">
                      <div className="w-28 bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${getStatusColor(pct)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                    </div>
                    {savedEvent === event.eventId && (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Saved
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expandable booth assignment panel */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 mr-1">Auto-assign:</span>
                        <button
                          onClick={() => handleFillEmpty(apps)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
                          title="Assign sequential numbers to vendors without booths"
                        >
                          <Wand2 className="w-3 h-3" />
                          Fill empty
                        </button>
                        <button
                          onClick={() => handleReassignAll(apps)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
                          title="Overwrite all booth assignments starting from 1"
                        >
                          <Wand2 className="w-3 h-3" />
                          Reassign all
                        </button>
                      </div>
                      <button
                        onClick={() => handleSaveEvent(event.eventId, apps)}
                        disabled={savingEvent === event.eventId}
                        className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium disabled:opacity-50 flex-shrink-0"
                      >
                        {savingEvent === event.eventId ? "Saving…" : "Save Changes"}
                      </button>
                    </div>

                    {/* Vendor rows */}
                    <div className="px-4 py-3 space-y-2">
                      {apps.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No vendors for this event yet.
                        </p>
                      ) : (
                        apps.map(app => {
                          const placement = placementEdits[app.id] || "";
                          return (
                            <div
                              key={app.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white rounded-lg px-3 py-2.5 border border-gray-200"
                            >
                              {/* Vendor info */}
                              <div className="flex items-center gap-2.5 min-w-0">
                                <img
                                  src={app.companyLogoUrl || placeholderImage}
                                  alt=""
                                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                                />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {app.businessName || app.fullName || "Unknown Vendor"}
                                    </p>
                                    {placementBadge(placement)}
                                  </div>
                                  <p className="text-xs text-gray-400 truncate">
                                    {app.businessType || ""}
                                  </p>
                                </div>
                              </div>

                              {/* Assignment controls */}
                              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                                {/* Booth number */}
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400">Booth #</span>
                                  <input
                                    type="text"
                                    value={boothEdits[app.id] ?? ""}
                                    onChange={e =>
                                      setBoothEdits(prev => ({
                                        ...prev,
                                        [app.id]: e.target.value,
                                      }))
                                    }
                                    placeholder="—"
                                    className="w-14 px-2 py-1 text-sm text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                  />
                                </div>

                                {/* Placement type */}
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <select
                                    value={placement}
                                    onChange={e =>
                                      setPlacementEdits(prev => ({
                                        ...prev,
                                        [app.id]: e.target.value,
                                      }))
                                    }
                                    className="text-xs border border-gray-300 rounded-lg px-1.5 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-700 bg-white"
                                  >
                                    {PLACEMENT_OPTIONS.map(opt => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BoothsContent;
